import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, uploadClubDocument } from "@/lib/api";
import { formatDate, daysTo, countdownLabel } from "@/lib/dates";
import { toast } from "sonner";
import { Page, PageHero, Card, SectionTitle, PrimaryButton, GhostButton, StatusPill, C, Loader } from "@/components/district/kit";
import { MockGovCard } from "@/components/GovBrief";
import { ClubDrawer } from "@/pages/district/GovVisits";
import { useT } from "@/i18n";
import {
  ArrowLeft, ClipboardText, ChatCircleText, ListChecks, UploadSimple, DownloadSimple, Trash,
  CheckCircle, WarningCircle, Plus, PaperPlaneTilt, PencilSimple, Lock, ChatText, BookOpen, X,
} from "@phosphor-icons/react";

const isImg = (n = "") => /\.(jpe?g|png)$/i.test(n);
const dlUrl = (id) => `${api.defaults.baseURL}/documents/${id}/download`;

// Same grouping as the club dashboard uploads page.
const DOC_GROUPS = ["Admin", "Financial"];
// Same rule as the club dashboard: board/committee rosters are NOT document tiles
// ("Board Meeting Attendance" stays a document since it is attendance, not a roster).
const isRosterDoc = (name) => /committee/i.test(name || "") || (/board/i.test(name || "") && !/meeting/i.test(name || ""));

// Dynamic gender buckets — driven by whatever labels the backend returns (Male/Female/Not given/
// Self-described, or any district-specific value), so no fixed 3-value assumption.
const G_ORDER = ["Male", "Female", "Not given", "Self-described"];
const G_COLORS = { Male: C.indigo, Female: C.cranberry, "Not given": "#9AA3AF", "Self-described": "#00A2BE",
  male: C.indigo, female: C.cranberry, other: "#00A2BE" };
const G_PALETTE = [C.indigo, C.cranberry, "#00A2BE", "#E8A33D", "#8A5A00", "#1E7A46", "#4B31C9"];
const gPretty = (k) => ({ male: "Male", female: "Female", other: "Other" }[k] || k);
function genderSeries(dict) {
  const d = dict || {};
  const keys = Object.keys(d);
  const ordered = [...G_ORDER.filter((k) => keys.includes(k)), ...keys.filter((k) => !G_ORDER.includes(k)).sort()];
  return ordered.map((k, i) => ({ key: k, label: gPretty(k), color: G_COLORS[k] || G_PALETTE[i % G_PALETTE.length], value: Number(d[k]) || 0 }));
}
function docGroup(name) {
  const n = (name || "").toLowerCase();
  if (/\btrf\b|contribution|foundation/.test(n)) return "TRF";
  if (/invoice|payment|dues|budget|audited|account|fee|\bsar\b|trust/.test(n)) return "Financial";
  return "Admin";
}

// Organogram builder for Board / Committees / Sub-committees (kind "both").
function RosterBuilder({ item, clubId, reload }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    api.get(`/clubs/${clubId}/roster/${item.item_id}`)
      .then((r) => { setData(r.data); setRows(r.data.positions || []); })
      .catch(() => setData({ positions: [], members: [], can_edit: false }));
  }, [clubId, item.item_id]);
  if (!data) return <Loader label={t("rv_loading_roster")} />;
  const memberName = (id) => (data.members.find((m) => m.member_id === id) || {}).full_name || "—";
  const set = (i, k, v) => { setRows((a) => a.map((r, idx) => idx === i ? { ...r, [k]: v } : r)); setDirty(true); };
  const add = () => { setRows((a) => [...a, { title: "", member_id: null }]); setDirty(true); };
  const del = (i) => { setRows((a) => a.filter((_, idx) => idx !== i)); setDirty(true); };
  const save = async () => {
    try { await api.put(`/clubs/${clubId}/roster/${item.item_id}`, { positions: rows.filter((r) => (r.title || "").trim()) }); toast.success("Roster saved"); setDirty(false); reload && reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data.can_edit) {
    return (
      <div className="space-y-1.5" data-testid={`roster-view-${item.item_id}`}>
        {rows.length === 0 && <p className="text-[14px]" style={{ color: C.sub }}>{t("rv_not_built")}</p>}
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between gap-2 rounded-xl bg-[#F7F7FD] px-3.5 py-2.5 text-[14px]">
            <span className="font-medium" style={{ color: C.ink }}>{r.title}</span>
            <span style={{ color: r.member_id ? C.ink : C.sub }}>{r.member_id ? memberName(r.member_id) : t("rv_vacant")}</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid={`roster-edit-${item.item_id}`}>
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <input value={r.title} onChange={(e) => set(i, "title", e.target.value)} placeholder={t("rv_position_ph")}
            className="w-44 shrink-0 rounded-2xl border px-3 py-2.5 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
          <select data-testid={`roster-member-${i}`} value={r.member_id || ""} onChange={(e) => set(i, "member_id", e.target.value || null)}
            className="min-w-0 flex-1 rounded-2xl border px-3 py-2.5 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }}>
            <option value="">{t("rv_vacant_opt")}</option>
            {data.members.map((m) => <option key={m.member_id} value={m.member_id}>{m.full_name}</option>)}
          </select>
          <button onClick={() => del(i)} className="grid h-10 w-10 shrink-0 place-items-center rounded-xl" style={{ color: C.cranberry }}><Trash size={14} weight="bold" /></button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <GhostButton data-testid={`roster-add-${item.item_id}`} onClick={add}><Plus size={14} weight="bold" /> {t("rv_add_position")}</GhostButton>
        {dirty && <PrimaryButton data-testid={`roster-save-${item.item_id}`} onClick={save}>{t("rv_save_roster")}</PrimaryButton>}
      </div>
    </div>
  );
}

function statusFor(item, brief) {
  const revs = (brief.reviews || []).filter((r) => r.item_id === item.item_id);
  const hasDoc = (brief.documents || []).some((d) => d.item_id === item.item_id);
  const hasInfo = !!(brief.info_answers?.[item.item_id]?.text || "").trim();
  const has = item.kind === "info" ? hasInfo : hasDoc;
  if (revs.some((r) => r.status === "attention")) return "attention";
  if (revs.some((r) => r.status === "checked")) return "accepted";
  if (revs.some((r) => r.status === "resubmitted")) return "resubmitted";
  if (has) return "submitted";
  return item.required ? "missing" : "optional";
}

function Requirement({ item, brief, reload }) {
  const { t } = useT();
  const [info, setInfo] = useState(brief.info_answers?.[item.item_id]?.text || "");
  const [editing, setEditing] = useState(false);
  const docs = (brief.documents || []).filter((d) => d.item_id === item.item_id);
  const revs = (brief.reviews || []).filter((r) => r.item_id === item.item_id);
  const st = statusFor(item, brief);
  const kindLabel = item.kind === "info" ? t("rv_kind_info") : item.kind === "template" ? t("rv_kind_template") : item.kind === "both" ? t("rv_kind_both") : t("rv_kind_doc");

  const upload = async (file) => {
    if (!file) return;
    try { await uploadClubDocument(brief.club_id, item.item_id, file); toast.success("Uploaded"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  const removeDoc = async (id) => { try { await api.delete(`/documents/${id}`); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const saveInfo = async () => { try { await api.put(`/clubs/${brief.club_id}/checklist/${item.item_id}/info`, { text: info }); toast.success("Saved"); setEditing(false); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const review = async (status) => {
    let note = "";
    if (status === "attention") { note = window.prompt(t("rv_prompt_improve")) || ""; }
    try { await api.post(`/clubs/${brief.club_id}/checklist/${item.item_id}/review`, { status, note }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: C.line }} data-testid={`req-${item.item_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[16px] font-medium" style={{ color: C.ink }}>{item.name} {!item.required && <span className="text-[13px]" style={{ color: C.sub }}>({t("rv_optional")})</span>}</p>
          <p className="text-[13px]" style={{ color: C.sub }}>{kindLabel}{item.instruction ? ` · ${item.instruction}` : ""}</p>
        </div>
        <StatusPill status={st} />
      </div>

      {/* Content */}
      <div className="mt-3">
        {item.kind === "info" ? (
          brief.can_edit && editing ? (
            <div>
              <textarea data-testid={`info-input-${item.item_id}`} value={info} onChange={(e) => setInfo(e.target.value)} rows={3}
                className="w-full rounded-lg border px-3 py-2 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
              <div className="mt-2 flex gap-2"><PrimaryButton data-testid={`info-save-${item.item_id}`} onClick={saveInfo}>{t("save")}</PrimaryButton>
                <button onClick={() => setEditing(false)} className="text-[14px] font-medium" style={{ color: C.sub }}>{t("rv_cancel")}</button></div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-2">
              <p className="text-[15px] whitespace-pre-wrap" style={{ color: info ? C.ink : C.sub }}>{info || t("rv_not_filled")}</p>
              {brief.can_edit && <button data-testid={`info-edit-${item.item_id}`} onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-[14px] font-medium" style={{ color: C.azure }}><PencilSimple size={14} weight="bold" /> {info ? t("rv_edit") : t("rv_fill_in")}</button>}
            </div>
          )
        ) : (
          <div className="space-y-2">
            {item.kind === "both" && (
              <div className="mb-3">
                <RosterBuilder item={item} clubId={brief.club_id} reload={reload} />
                <p className="mt-3 mb-1 text-[13px]" style={{ color: C.sub }}>{t("rv_or_upload")}</p>
              </div>
            )}
            {docs.map((d) => (
              <div key={d.doc_id} className="flex items-center gap-3 rounded-lg bg-[#F7FAFF] p-2" data-testid={`doc-${d.doc_id}`}>
                {isImg(d.file_name)
                  ? <img src={dlUrl(d.doc_id)} alt={d.file_name} className="h-12 w-12 rounded-lg object-cover" />
                  : <span className="grid h-12 w-12 place-items-center rounded-lg bg-white text-[11px] font-bold" style={{ color: C.cranberry }}>PDF</span>}
                <a href={dlUrl(d.doc_id)} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-[14px] font-medium" style={{ color: C.azure }}>{d.file_name}</a>
                <a href={dlUrl(d.doc_id)} target="_blank" rel="noreferrer" className="grid h-8 w-8 place-items-center rounded-lg" style={{ color: C.azure }} data-testid={`dl-${d.doc_id}`}><DownloadSimple size={16} weight="bold" /></a>
                {brief.can_edit && <button onClick={() => removeDoc(d.doc_id)} className="grid h-8 w-8 place-items-center rounded-lg" style={{ color: C.cranberry }}><Trash size={15} weight="bold" /></button>}
              </div>
            ))}
            {docs.length === 0 && <p className="text-[14px]" style={{ color: C.sub }}>{t("rv_no_file")}</p>}
            {brief.can_edit && (
              <label data-testid={`upload-${item.item_id}`} className="inline-flex cursor-pointer items-center gap-1.5 text-[14px] font-medium" style={{ color: C.azure }}>
                <UploadSimple size={15} weight="bold" /> {docs.length ? t("rv_upload_more") : t("rv_upload_first")}
                <input type="file" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xlsx,.xls" className="hidden" onChange={(e) => upload(e.target.files[0])} />
              </label>
            )}
          </div>
        )}
      </div>

      {/* Review comments + history */}
      {revs.map((r) => (
        <div key={r.reviewer_id} className="mt-3 rounded-lg p-2.5 text-[14px]" style={{ background: r.status === "attention" ? "#FDECEC" : "#F2F6FC" }} data-testid={`review-${item.item_id}`}>
          <p className="font-medium" style={{ color: r.status === "attention" ? C.cranberry : C.ink }}>
            {r.status === "attention" ? t("rv_needs_improvement") : r.status === "resubmitted" ? t("rv_awaiting_rereview") : t("rv_marked_ok")}
          </p>
          {r.note && <p className="mt-0.5" style={{ color: C.ink }}>“{r.note}”</p>}
          {(r.history || []).length > 0 && (
            <ul className="mt-1 space-y-0.5 border-t pt-1" style={{ borderColor: "#0000000f" }}>
              {r.history.map((h, i) => (
                <li key={i} style={{ color: C.sub }}>{h.kind === "corrected" ? "↳ Club corrected this item" : `Earlier: ${h.status === "attention" ? "needs improvement" : "OK"}${h.note ? ` — “${h.note}”` : ""}`}</li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {/* Review decision — approve or send back */}
      {brief.is_official && !brief.completed && (
        <div className="mt-4 flex gap-2 border-t pt-4" style={{ borderColor: C.line }}>
          <button data-testid={`ok-${item.item_id}`} onClick={() => review("checked")} className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-[14px] font-semibold" style={{ background: "#E7F6EE", color: C.green }}><CheckCircle size={16} weight="bold" /> {t("rv_approved")}</button>
          <button data-testid={`attn-${item.item_id}`} onClick={() => review("attention")} className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-[14px] font-semibold" style={{ background: "#FDECEC", color: C.cranberry }}><WarningCircle size={16} weight="bold" /> {t("rv_not_approved")}</button>
        </div>
      )}
    </div>
  );
}

// Compact document tile (club-dashboard iconography) with a View button.
function DocTile({ item, brief, onView }) {
  const { t } = useT();
  const st = statusFor(item, brief);
  const done = st === "accepted";
  const attn = st === "attention";
  const has = st !== "missing" && st !== "optional";
  const Icon = done ? CheckCircle : attn ? WarningCircle : ClipboardText;
  const color = done ? "#0F8A5F" : attn ? C.cranberry : C.indigoText;
  const bg = done ? "#DDF3E8" : attn ? "#FDECEC" : C.indigoSoft;
  return (
    <div className="flex flex-col items-center gap-1.5 rounded-2xl border p-2.5 text-center" style={{ borderColor: C.line, background: has ? "#FBFBFE" : "#FFFFFF" }} data-testid={`rev-tile-${item.item_id}`}>
      <span className="grid h-9 w-9 place-items-center rounded-xl" style={{ background: bg, color }}><Icon size={17} weight={done || attn ? "fill" : "bold"} /></span>
      <span className="line-clamp-2 text-[11.5px] font-semibold leading-tight" style={{ color: C.ink }}>{item.name}</span>
      <button data-testid={`rev-view-${item.item_id}`} onClick={() => onView(item)} className="mt-0.5 rounded-full px-3 py-1 text-[12px] font-bold" style={{ color: C.indigoText, background: C.indigoSoft }}>{t("rv_view")}</button>
    </div>
  );
}

function MiniDonut({ data, size = 92 }) {
  const total = data.reduce((a, b) => a + (b.value || 0), 0);
  if (!total) return <div className="grid place-items-center text-[13px]" style={{ width: size, height: size, color: C.sub }}>—</div>;
  let acc = 0;
  const stops = data.filter((d) => d.value > 0).map((d) => {
    const from = (acc / total) * 360; acc += d.value; const to = (acc / total) * 360;
    return `${d.color} ${from}deg ${to}deg`;
  }).join(", ");
  return (
    <div className="relative rounded-full" style={{ width: size, height: size, background: `conic-gradient(${stops})` }}>
      <div className="absolute rounded-full bg-white grid place-items-center" style={{ inset: "22%" }}>
        <span className="text-[15px] font-extrabold" style={{ color: C.ink }}>{total}</span>
      </div>
    </div>
  );
}

function MembershipInfographic({ brief }) {
  const { t } = useT();
  const gm = brief.gender_mix || {};
  const toData = (grp) => genderSeries(gm[grp]);
  const blocks = [[t("club_total_membership"), "members"], [t("club_board"), "board"]];
  if (gm.inducted) blocks.push([t("rv_new_inductees"), "inducted"]);
  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: C.line, background: "#fff" }} data-testid="review-membership">
      <h3 className="mb-3 text-[15px] font-bold tracking-tight" style={{ color: C.ink }}>{t("gp_membership")}</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        {blocks.map(([label, grp]) => {
          const data = toData(grp);
          return (
            <div key={grp} className="flex items-center gap-3">
              <MiniDonut data={data} />
              <div>
                <p className="mb-1 text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{label}</p>
                {data.map((d) => (
                  <p key={d.label} className="flex items-center gap-1.5 text-[12.5px]" style={{ color: C.ink }}>
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: d.color }} /> {d.label} · <b>{d.value}</b>
                  </p>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HeroMembership({ brief }) {
  const { t } = useT();
  const series = genderSeries((brief.gender_mix && brief.gender_mix.members) || {});
  const total = series.reduce((a, b) => a + b.value, 0);
  const R = 34, CIRC = 2 * Math.PI * R;
  let acc = 0;
  return (
    <div className="hidden items-center gap-5 self-center lg:flex" data-testid="hero-membership">
      <div className="space-y-1.5 text-right">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-white/70">{t("club_gender_mix")}</p>
        {series.map((sg) => (
          <div key={sg.key} className="flex items-center justify-end gap-2 text-[13px] font-semibold text-white">
            <span>{sg.label} · {sg.value}</span>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: sg.color }} />
          </div>
        ))}
      </div>
      <svg width="90" height="90" viewBox="0 0 90 90">
        <circle cx="45" cy="45" r={R} fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="11" />
        {total > 0 && series.map((sg) => {
          const frac = sg.value / total; if (!frac) return null;
          const seg = <circle key={sg.key} cx="45" cy="45" r={R} fill="none" stroke={sg.color} strokeWidth="11"
            strokeDasharray={`${frac * CIRC} ${CIRC - frac * CIRC}`} strokeDashoffset={-acc * CIRC} transform="rotate(-90 45 45)" />;
          acc += frac; return seg;
        })}
        <text x="45" y="50" textAnchor="middle" style={{ fontSize: 19, fontWeight: 800, fill: "#fff" }}>{total}</text>
      </svg>
    </div>
  );
}


function Discussion({ brief, reload }) {
  const { t } = useT();
  const disc = brief.discussion || { points: [], categories: [], sees_all: false };
  const [cat, setCat] = useState(disc.categories?.[0] || "");
  const [text, setText] = useState("");
  const [points, setPoints] = useState(disc.points || []);
  useEffect(() => { setPoints((brief.discussion && brief.discussion.points) || []); }, [brief]);
  const add = async () => {
    const value = text.trim();
    if (!value) return;
    try {
      const r = await api.post(`/clubs/${brief.club_id}/discussion`, { category: cat, text: value });
      if (r.data && r.data.point) setPoints((prev) => [...prev, r.data.point]);
      setText("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const resolve = async (pid, resolved) => {
    setPoints((prev) => prev.map((p) => (p.point_id === pid ? { ...p, resolved } : p)));
    try { await api.post(`/discussion/${pid}/resolve`, { resolved }); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); reload(); }
  };
  const note = async (pid) => { const n = window.prompt(t("rv_prompt_note")) || ""; setPoints((prev) => prev.map((p) => (p.point_id === pid ? { ...p, dg_note: n } : p))); try { await api.put(`/discussion/${pid}/note`, { note: n }); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); reload(); } };
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const startEdit = (p) => { setEditId(p.point_id); setEditVal(p.text); };
  const saveEdit = async (pid) => {
    const v = editVal.trim(); if (!v) return;
    setPoints((prev) => prev.map((x) => (x.point_id === pid ? { ...x, text: v } : x)));
    setEditId(null);
    try { await api.put(`/discussion/${pid}`, { text: v }); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); reload(); }
  };
  const del = async (pid) => {
    setPoints((prev) => prev.filter((x) => x.point_id !== pid));
    try { await api.delete(`/discussion/${pid}`); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); reload(); }
  };

  const open = points.filter((p) => !p.resolved);
  const resolved = points.filter((p) => p.resolved);
  const Row = (p) => (
    <div key={p.point_id} className="rounded-2xl border p-3" style={{ borderColor: C.line }} data-testid={`point-${p.point_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {editId === p.point_id ? (
            <div className="flex flex-col gap-2">
              <input data-testid={`point-edit-input-${p.point_id}`} value={editVal} onChange={(e) => setEditVal(e.target.value)} autoFocus
                className="w-full rounded-lg border px-3 py-2 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} />
              <div className="flex gap-3 text-[14px] font-semibold">
                <button data-testid={`point-save-${p.point_id}`} onClick={() => saveEdit(p.point_id)} style={{ color: C.green }}>Save</button>
                <button data-testid={`point-cancel-${p.point_id}`} onClick={() => setEditId(null)} style={{ color: C.sub }}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-[15px]" style={{ color: C.ink }}>{p.text}</p>
              <p className="mt-0.5 text-[13px]" style={{ color: C.sub }}>{p.category}{p.author_name ? ` · ${p.author_name}` : ""}</p>
            </>
          )}
        </div>
        {p.resolved ? <StatusPill status="ok">{t("rv_resolved")}</StatusPill> : <StatusPill status="in_progress">{t("rv_open")}</StatusPill>}
      </div>
      {p.dg_note && <p className="mt-2 rounded-lg bg-[#F2F6FC] p-2 text-[14px]" style={{ color: C.ink }}><b>{t("rv_note")}:</b> {p.dg_note}</p>}
      {disc.sees_all && !brief.completed && editId !== p.point_id && (
        <div className="mt-2 flex flex-wrap gap-3 text-[14px] font-medium">
          <button onClick={() => note(p.point_id)} style={{ color: C.azure }} className="inline-flex items-center gap-1"><ChatText size={14} weight="bold" /> {t("rv_note")}</button>
          <button onClick={() => resolve(p.point_id, !p.resolved)} style={{ color: p.resolved ? C.sub : C.green }} className="inline-flex items-center gap-1"><CheckCircle size={14} weight="bold" /> {p.resolved ? t("rv_reopen") : t("rv_resolve")}</button>
          <button data-testid={`point-edit-${p.point_id}`} onClick={() => startEdit(p)} style={{ color: C.azure }} className="inline-flex items-center gap-1"><PencilSimple size={14} weight="bold" /> Edit</button>
          <button data-testid={`point-delete-${p.point_id}`} onClick={() => del(p.point_id)} style={{ color: "#E5484D" }} className="inline-flex items-center gap-1"><Trash size={14} weight="bold" /> Delete</button>
        </div>
      )}
    </div>
  );

  return (
    <Card>
      <SectionTitle icon={ChatCircleText} title={t("rv_discussion_points")} sub={t("rv_discussion_sub")} />
      {!brief.completed && (
        <div className="mb-4 flex flex-col gap-2">
          <select data-testid="disc-cat" value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-lg border px-3 py-2 text-[14px]" style={{ borderColor: C.line }}>
            {(disc.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input data-testid="disc-text" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("rv_add_point")}
            className="min-w-0 flex-1 rounded-lg border px-3 py-2 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
          <PrimaryButton data-testid="disc-add" onClick={add}><Plus size={15} weight="bold" /> {t("rv_add")}</PrimaryButton>
        </div>
      )}
      {points.length === 0 && <p className="text-[15px]" style={{ color: C.sub }}>{t("rv_no_points")}</p>}
      {open.length > 0 && <><p className="mb-2 text-[13px] font-medium uppercase tracking-wide" style={{ color: C.amber }}>{t("rv_open")}</p><div className="space-y-2">{open.map(Row)}</div></>}
      {resolved.length > 0 && <><p className="mb-2 mt-4 text-[13px] font-medium uppercase tracking-wide" style={{ color: C.green }}>{t("rv_resolved")}</p><div className="space-y-2">{resolved.map(Row)}</div></>}
    </Card>
  );
}

function ActionPoints({ brief, reload, patch }) {
  const { t } = useT();
  const [pts, setPts] = useState(brief.action_points || []);
  const closed = brief.completed;
  const shared = brief.action_points_shared;
  const canClose = brief.can_complete; // DG, open visit
  const editable = brief.is_official && (!closed || !shared); // open, or closed + recalled
  const upd = (i, k, v) => setPts((a) => a.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const add = () => setPts((a) => [...a, { text: "", assignee: "", instructions: "" }]);
  const del = (i) => setPts((a) => a.filter((_, idx) => idx !== i));
  const save = async () => { try { const r = await api.put(`/clubs/${brief.club_id}/gov/action-points`, { action_points: pts.filter((p) => p.text.trim()) }); setPts(r.data.action_points); toast.success("Action points saved"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const [asking, setAsking] = useState(false);
  const close = async (share) => { setAsking(false); patch({ completed: true, action_points_shared: share }); try { await api.post(`/clubs/${brief.club_id}/gov/complete`, { share_with_club: share }); toast.success(share ? "GOV closed · action points shared with club" : "GOV closed · action points kept private"); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); reload(); } };
  const toggleShare = async (share) => { patch({ action_points_shared: share }); try { await api.put(`/clubs/${brief.club_id}/gov/action-points/share`, { shared: share }); toast.success(share ? "Shared with the club" : "Recalled from the club"); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); reload(); } };

  return (
    <Card>
      <SectionTitle icon={ListChecks} title={t("rv_final_action_points")} sub={t("rv_action_sub")}
        right={<span className="flex items-center gap-2">
          {brief.action_points_shared && <span data-testid="shared-badge" className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-bold" style={{ background: "#DDF3E8", color: "#0F8A5F" }}>{t("rv_shared_with_club")}</span>}
          {brief.completed && <StatusPill status="completed">{t("rv_gov_closed")}</StatusPill>}
        </span>} />
      <div className="space-y-2" data-testid="action-points">
        {pts.map((p, i) => (
          <div key={i} className="rounded-2xl border p-3" style={{ borderColor: C.line }} data-testid={`ap-${i}`}>
            {editable ? (
              <div className="space-y-2">
                <input data-testid={`ap-text-${i}`} value={p.text} onChange={(e) => upd(i, "text", e.target.value)} placeholder={t("rv_action_point_ph")}
                  className="w-full rounded-lg border px-3 py-2 text-[15px] font-medium outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input value={p.assignee} onChange={(e) => upd(i, "assignee", e.target.value)} placeholder={t("rv_assigned_to_ph")}
                    className="rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF] sm:w-48" style={{ borderColor: C.line }} />
                  <input value={p.instructions} onChange={(e) => upd(i, "instructions", e.target.value)} placeholder={t("rv_instructions_ph")}
                    className="min-w-0 flex-1 rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
                  <button onClick={() => del(i)} className="grid h-9 w-9 place-items-center rounded-lg" style={{ color: C.cranberry }}><Trash size={15} weight="bold" /></button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-[15px] font-medium" style={{ color: C.ink }}>{p.text}</p>
                <p className="text-[14px]" style={{ color: C.sub }}>{p.assignee ? `${t("rv_assigned_to")} ${p.assignee}` : t("rv_unassigned")}{p.instructions ? ` · ${p.instructions}` : ""}</p>
              </div>
            )}
          </div>
        ))}
        {pts.length === 0 && <p className="text-[15px]" style={{ color: C.sub }}>{t("rv_no_action_points")}</p>}
      </div>
      {editable && !asking && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <GhostButton data-testid="ap-add" onClick={add}><Plus size={15} weight="bold" /> {t("rv_add_action_point")}</GhostButton>
          <PrimaryButton data-testid="ap-save" onClick={save}>{t("rv_save_action_points")}</PrimaryButton>
          {canClose && (
            <button data-testid="close-gov" onClick={() => setAsking(true)} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[15px] font-bold text-white" style={{ background: C.gold, color: C.goldInk }}>
              <Lock size={15} weight="bold" /> {t("rv_close_gov")}
            </button>
          )}
        </div>
      )}
      {canClose && asking && (
        <div className="mt-4 rounded-2xl border p-4" style={{ borderColor: C.line, background: "#FBFBFE" }} data-testid="close-share-prompt">
          <div className="mb-3 flex items-start gap-2 rounded-xl px-3 py-2.5" style={{ background: "#FFF6E6", color: "#8A5A00" }} data-testid="close-readonly-warning">
            <WarningCircle size={18} weight="bold" className="mt-0.5 shrink-0" />
            <p className="text-[13px] font-semibold leading-snug">{t("rv_close_warning")}</p>
          </div>
          <p className="text-[15px] font-semibold" style={{ color: C.ink }}>{t("rv_share_prompt")}</p>
          <p className="mt-0.5 text-[13px]" style={{ color: C.sub }}>{t("rv_share_prompt_sub")}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="close-private" onClick={() => close(false)} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[14px] font-bold text-white" style={{ background: C.gold, color: C.goldInk }}><Lock size={14} weight="bold" /> {t("rv_close_private")}</button>
            <button data-testid="close-share" onClick={() => close(true)} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[14px] font-bold text-white" style={{ background: C.indigo }}>{t("rv_close_share")}</button>
            <button data-testid="close-cancel" onClick={() => setAsking(false)} className="rounded-full px-4 py-2 text-[14px] font-semibold" style={{ color: C.sub }}>{t("rv_cancel")}</button>
          </div>
        </div>
      )}
      {brief.is_official && closed && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t pt-4" style={{ borderColor: C.line }} data-testid="share-status">
          <span className="text-[14px] font-semibold" style={{ color: shared ? "#0F8A5F" : C.sub }}>
            {shared ? t("rv_shared_status") : t("rv_private_status")}
          </span>
          {shared
            ? <button data-testid="recall-btn" onClick={() => toggleShare(false)} className="rounded-full px-4 py-2 text-[14px] font-bold" style={{ color: C.cranberry, background: "#FDECEC" }}>{t("rv_recall")}</button>
            : <button data-testid="share-btn" onClick={() => toggleShare(true)} className="rounded-full px-4 py-2 text-[14px] font-bold" style={{ color: C.indigoText, background: C.indigoSoft }}>{t("rv_share")}</button>}
        </div>
      )}
    </Card>
  );
}

function DirectoryButton({ clubId }) {
  const { t } = useT();
  const url = `${api.defaults.baseURL}/clubs/${clubId}/directory.pdf`;
  return (
    <a data-testid="club-directory-btn" href={url} target="_blank" rel="noreferrer"
      className="inline-flex min-h-[40px] items-center gap-2 rounded-full px-4 text-[14px] font-medium"
      style={{ color: C.indigo, boxShadow: "inset 0 0 0 1.5px " + C.line }}>
      <BookOpen size={15} weight="bold" /> {t("rv_directory_pdf")}
    </a>
  );
}

// Governor -> club officers notification (President/Secretary, cc AG) with visit status + comment.
const NOTIFY_PRESETS = ["Documents overdue — please upload ASAP.", "Great progress — thank you!",
  "Please confirm your GOV visit date.", "A few items still pending — see your dashboard."];

function fmtNotified(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return null; }
}

function NotifyOfficials({ clubId, brief, onNotified }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const hasOfficials = !!(brief.president || brief.secretary);
  const lastAt = fmtNotified(brief.last_notified_at);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clubs/${clubId}/notify-officials`, { comment });
      toast.success(t("rv_notify_sent")); setOpen(false); setComment("");
      onNotified?.(r.data?.last_notified_at);
    } catch (e) { toast.error(e.response?.data?.detail || t("rv_notify_failed")); }
    setBusy(false);
  };
  return (
    <div className="mt-4 border-t pt-4" style={{ borderColor: C.line }}>
      {!open ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <button data-testid="notify-officials-btn" onClick={() => setOpen(true)} disabled={!hasOfficials}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[14px] font-semibold text-white transition-opacity disabled:opacity-40"
            style={{ background: C.azure }} title={hasOfficials ? "" : t("rv_notify_no_officials")}>
            <PaperPlaneTilt size={16} weight="fill" /> {t("rv_notify_officials")}
          </button>
          {lastAt && <span data-testid="notify-last-at" className="text-[12px]" style={{ color: C.sub }}>{t("rv_notify_last")} {lastAt}</span>}
        </div>
      ) : (
        <div className="space-y-2" data-testid="notify-officials-panel">
          <p className="text-[13px]" style={{ color: C.sub }}>{t("rv_notify_hint")}</p>
          <div className="flex flex-wrap gap-1.5">
            {NOTIFY_PRESETS.map((pre, i) => (
              <button key={i} data-testid={`notify-preset-${i}`} onClick={() => setComment(pre)}
                className="rounded-full border px-3 py-1 text-[12px] font-medium transition-colors hover:bg-[#EEF3FB]"
                style={{ borderColor: C.line, color: C.ink }}>{pre}</button>
            ))}
          </div>
          <textarea data-testid="notify-comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
            rows={3} placeholder={t("rv_notify_placeholder")}
            className="w-full rounded-lg border p-2.5 text-[14px] outline-none" style={{ borderColor: C.line, color: C.ink }} />
          <div className="flex gap-2">
            <button data-testid="notify-send-btn" onClick={send} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-50" style={{ background: C.azure }}>
              <PaperPlaneTilt size={15} weight="fill" /> {busy ? t("rv_notify_sending") : t("rv_notify_send")}
            </button>
            <button data-testid="notify-cancel-btn" onClick={() => { setOpen(false); setComment(""); }}
              className="rounded-full px-4 py-2 text-[14px] font-semibold" style={{ color: C.sub }}>{t("rv_cancel")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// One-tap: create (if needed) & email login invitations to the club's President & Secretary.
function InviteOfficers({ clubId }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const invite = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clubs/${clubId}/invite-officers`, {});
      const sent = (r.data?.results || []).filter((x) => x.status === "sent").length;
      const active = (r.data?.results || []).filter((x) => x.status === "already_active").length;
      toast.success(sent ? t("rv_invite_sent").replace("{n}", sent) : (active ? t("rv_invite_active") : t("rv_invite_none")));
    } catch (e) { toast.error(e.response?.data?.detail || t("rv_invite_failed")); }
    setBusy(false);
  };
  return (
    <button data-testid="invite-officers-btn" onClick={invite} disabled={busy}
      className="mt-3 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[14px] font-semibold transition-colors hover:bg-[#EEF3FB] disabled:opacity-50"
      style={{ borderColor: C.azure, color: C.azure }}>
      <PaperPlaneTilt size={15} weight="bold" /> {busy ? t("rv_invite_sending") : t("rv_invite_officers")}
    </button>
  );
}


export default function ClubReview({ clubId: clubIdProp, embedded = false, onClose }) {
  const { t } = useT();
  const params = useParams();
  const navigate = useNavigate();
  const clubId = clubIdProp || params.clubId;
  const [brief, setBrief] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const load = () => api.get(`/clubs/${clubId}/brief`).then((r) => setBrief(r.data)).catch(() => setBrief({ error: true }));
  useEffect(() => { load(); }, [clubId]);

  const title = brief?.club_name || t("rv_club_report");
  const submitted = brief?.prep_status === "submitted";
  const subtitle = !brief ? "" : brief.completed ? t("rv_closed_ro")
    : submitted ? (brief.submitted_incomplete ? t("rv_submitted_incomplete") : t("rv_submitted_by_club"))
    : t("rv_prep_in_progress");
  const close = onClose || (() => navigate("/"));

  const items = brief?.items || [];
  const mainContent = !brief ? null : (
    <>
      {brief.can_edit && <div className="flex flex-wrap items-center gap-2"><DirectoryButton clubId={clubId} /></div>}
      {DOC_GROUPS.map((g) => {
        const gItems = items.filter((it) => docGroup(it.name) === g && !isRosterDoc(it.name));
        if (gItems.length === 0) return null;
        const done = gItems.filter((it) => statusFor(it, brief) === "accepted").length;
        return (
          <div key={g} className="rounded-2xl border p-4" style={{ borderColor: C.line }} data-testid={`req-group-${g.toLowerCase()}`}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-[15px] font-bold tracking-tight" style={{ color: C.ink }}>{g === "Admin" ? t("club_docs_admin") : g === "Financial" ? t("club_docs_financial") : g}</h3>
              <span className="text-[13px] font-semibold" style={{ color: C.sub }}>{done}/{gItems.length}</span>
            </div>
            <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4 lg:grid-cols-6">
              {gItems.map((it) => <DocTile key={it.item_id} item={it} brief={brief} onView={setViewItem} />)}
            </div>
          </div>
        );
      })}
      {items.length === 0 && <Card><p className="text-[15px]" style={{ color: C.sub }}>{t("rv_no_requirements")}</p></Card>}
    </>
  );
  const sideContent = !brief ? null : (
    <>
      <Discussion brief={brief} reload={load} />
      {brief.is_official && (
        <Card data-testid="gov-visit-card">
          <SectionTitle icon={PaperPlaneTilt} title={t("rv_gov_visit_inv")} />
          <ClubDrawer inline canInitiate={brief.can_initiate} onChanged={load}
            club={{ club_id: clubId, club_name: title, zone_name: brief.zone_name, visit: brief.visit, president: brief.president, secretary: brief.secretary }} />
          <InviteOfficers clubId={clubId} />
          <NotifyOfficials clubId={clubId} brief={brief}
            onNotified={(at) => setBrief((b) => ({ ...b, last_notified_at: at }))} />
        </Card>
      )}
      <MockGovCard clubId={clubId} mock={brief.mock_gov} reload={load} canParticipate={brief.mock_can_participate} />
      {(brief.visit && brief.is_official) && <ActionPoints brief={brief} reload={load} patch={(u) => setBrief((b) => ({ ...b, ...u }))} />}
    </>
  );

  const body = !brief ? <Loader /> : brief.error ? (
    <Card><p className="text-[15px]" style={{ color: C.cranberry }}>{t("rv_no_access")}</p></Card>
  ) : embedded ? (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,600px)_minmax(0,1fr)]">
      <div className="space-y-5">{mainContent}</div>
      <div className="space-y-5">{sideContent}</div>
    </div>
  ) : (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="space-y-5 lg:col-span-2">{mainContent}</div>
      <div className="space-y-5 lg:col-span-1">{sideContent}</div>
    </div>
  );

  const viewModal = viewItem && (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setViewItem(null)} data-testid="doc-viewer">
      <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 className="text-[17px] font-bold tracking-tight" style={{ color: C.ink }} data-testid="doc-viewer-title">{viewItem.name}</h3>
          <button data-testid="doc-viewer-close" onClick={() => setViewItem(null)} className="grid h-8 w-8 shrink-0 place-items-center rounded-full" style={{ background: C.bg }}><X size={16} weight="bold" /></button>
        </div>
        <Requirement item={viewItem} brief={brief} reload={load} />
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-3 border-b px-5 py-4" style={{ borderColor: C.line }}>
          <div className="min-w-0">
            <h2 className="font-display text-[20px] font-medium tracking-tight" style={{ color: C.ink }} data-testid="panel-club-name">{title}</h2>
            {subtitle && <p className="text-[14px]" style={{ color: C.sub }}>{subtitle}</p>}
          </div>
          <button data-testid="panel-close" onClick={close} className="grid h-9 w-9 shrink-0 place-items-center rounded-full" style={{ background: C.bg }}><X size={18} weight="bold" /></button>
        </div>
        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">{body}</div>
        {viewModal}
      </div>
    );
  }
  const gm = (brief && brief.gender_mix && brief.gender_mix.members) || {};
  const bgm = (brief && brief.gender_mix && brief.gender_mix.board) || {};
  const gmS = (g) => {
    const s = genderSeries(g);
    const tt = s.reduce((a, b) => a + b.value, 0);
    return tt ? s.filter((x) => x.value).map((x) => `${x.value} ${x.label[0]}`).join(" · ") : "—";
  };
  const visitIso = brief && brief.visit && brief.visit.scheduled_start_utc;
  const mock = brief && brief.mock_gov;
  const stats = brief && !brief.error ? [
    { value: visitIso ? formatDate(visitIso) : t("rv_not_set"), label: visitIso && daysTo(visitIso) >= 0 ? countdownLabel(visitIso) : t("dist_col_gov_date") },
    { value: mock ? (mock.status === "confirmed" ? t("rv_confirmed") : t("club_proposed")) : "—", label: t("club_mock_gov") },
    { value: brief.member_count ?? 0, label: t("rv_members") },
    { value: brief.registered ?? 0, label: t("rv_registered_cap") },
    { value: gmS(gm), label: t("club_gender_mix") },
    { value: gmS(bgm), label: t("rv_board_gender") },
  ] : null;
  return (
    <div className="space-y-6">
      <nav className="flex flex-wrap items-center gap-1.5 text-[14px] font-medium" data-testid="review-breadcrumb">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5" style={{ color: C.indigo }}><ArrowLeft size={15} weight="bold" /> {t("rv_breadcrumb_district")}</button>
        {brief?.zone_name && (
          <>
            <span style={{ color: C.sub }}>/</span>
            <button data-testid="crumb-zone" onClick={() => navigate(`/?zone=${brief.zone_id}`)} style={{ color: C.indigo }}>{brief.zone_name}</button>
          </>
        )}
        <span style={{ color: C.sub }}>/</span>
        <span style={{ color: C.sub }}>{title}</span>
      </nav>
      {brief && !brief.error && (
        <div className="relative overflow-hidden rounded-2xl px-6 py-6 text-white md:px-8 md:py-7" data-testid="club-hero"
          style={{ background: "linear-gradient(135deg,#7C4DFF 0%,#5B34D6 100%)" }}>
          <div className="flex items-start justify-between gap-5">
            <div className="min-w-0">
              <img src={`${process.env.PUBLIC_URL || ""}/rotary-reverse.png`} alt="Rotary" className="mb-4 h-7 w-auto object-contain" />
              <h1 className="text-[26px] font-bold leading-tight tracking-tight md:text-[32px]">{title}</h1>
              {subtitle && <p className="mt-1.5 text-[15px] font-medium text-white/85">{subtitle}</p>}
            </div>
            <HeroMembership brief={brief} />
          </div>
          <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl bg-white/12 px-3.5 py-3 backdrop-blur-sm">
                <p className="text-[17px] font-bold leading-tight">{s.value}</p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-white/75">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {body}
      {viewModal}
    </div>
  );
}
